/**
 * ui.ts — side panel for active-perception.
 *
 * Tighter than vacuum-demo's: no reward-weight sliders (the policy is frozen,
 * the env's reward function is not live-tunable), no λ/γ sliders (no FQI).
 * What the panel exposes:
 *
 *   - Paint tools: gold (graded), stone, free, generate-clustered.
 *   - Visibility toggles: belief heat-map, sensor footprint overlay, reveal-all.
 *   - Speed slider (Hz of the agent tick).
 *   - Lifecycle: Start / Stop / Reset.
 *   - Comparator: "Run 30 episodes" → side-by-side trained-vs-random output.
 *   - Stats: collected value, steps, episodes, current Q-values.
 */

import { MAX_STEPS, N_ACTIONS } from './environment';
import type { LoopGateDiagnosticsForHud } from './agent';

export type Brush = 'gold' | 'stone' | 'free';

export interface PanelCallbacks {
  onBrush(b: Brush): void;
  onGenerate(): void;     // random clustered layout
  onClear(): void;
  onSpeed(hz: number): void;
  onReveal(b: boolean): void;
  onShowBelief(b: boolean): void;
  onShowSensor(b: boolean): void;
  onStart(): void;
  onStop(): void;
  onReset(): void;
  onCompare(): Promise<void>;   // run the head-to-head (async)
  onToggleGate?(enabled: boolean): void;  // toggle the LoopGate at runtime
  onToggleTaboo?(enabled: boolean): void;  // toggle the basin-cell taboo mask
  /** Switch the active trained model. Stops any running episode and resets
   *  the recurrence on the new model. */
  onSelectModel?(variant: 'base' | 'mem'): void;
}

export type ModelVariant = 'base' | 'mem';

export interface PanelUpdate {
  running: boolean;
  steps: number;
  collected: number;        // gold value collected this episode
  valueLeft: number;        // gold value remaining on the grid
  episodes: number;         // episodes this run
  totalCollected: number;   // cumulative across all episodes
  qValues: ArrayLike<number>;    // last tick's Q (for the 8-bar display)
  hNorm: number;            // last tick's hidden-state norm
  lastRatio: number | null; // last comparison result, if any
  comparing: boolean;       // currently running the comparator?
  /** Loop-gate HUD: null when the gate is disabled. */
  gate: LoopGateDiagnosticsForHud | null;
  /** Whether the LoopGate is enabled (drives the toggle's checked state). */
  gateEnabled: boolean;
  /** Whether the basin-cell taboo mask is enabled. */
  tabooEnabled: boolean;
  /** Currently-active model variant (drives the selector highlight). */
  activeModel: ModelVariant;
}

const DIRECTIONS_LABELS = ['E', 'W', 'S', 'N', 'SE', 'SW', 'NE', 'NW'];

export class SidePanel {
  readonly el: HTMLElement;
  private readonly cb: PanelCallbacks;

  // Cached DOM handles for hot-path updates (avoid re-querying each tick).
  private collectedEl!: HTMLElement;
  private valueLeftEl!: HTMLElement;
  private stepsEl!: HTMLElement;
  private episodesEl!: HTMLElement;
  private totalCollectedEl!: HTMLElement;
  private hNormEl!: HTMLElement;
  private qBarsEl!: HTMLElement;
  private ratioEl!: HTMLElement;
  private compareBtn!: HTMLButtonElement;
  private startBtn!: HTMLButtonElement;
  private stopBtn!: HTMLButtonElement;
  private resetBtn!: HTMLButtonElement;
  private gateToggleEl!: HTMLInputElement;
  private gateStatusEl!: HTMLElement;
  private gateFiresEl!: HTMLElement;
  private gateMaskedEl!: HTMLElement;
  private gateSpeedEl!: HTMLElement;
  private gateTabooToggleEl!: HTMLInputElement;
  private gateTabooEl!: HTMLElement;
  private modelBaseBtn!: HTMLButtonElement;
  private modelMemBtn!: HTMLButtonElement;

  constructor(cb: PanelCallbacks) {
    this.cb = cb;
    this.el = document.createElement('aside');
    this.el.className = 'panel';
    this.el.innerHTML = this.html();
    this.wire();
  }

  private html(): string {
    return `
      <div class="panel-section">
        <h3>Model <span class="section-sub">(trained policy)</span></h3>
        <div class="button-row">
          <button id="model-base" class="action-btn model-btn active" title="Baseline GRU DRQN (gru.tflite, 2.97× random)">Baseline GRU</button>
          <button id="model-mem" class="action-btn model-btn" title="GRU DRQN + visited-cells memory (gru_mem.tflite, 3.22× random)">GRU + visited map</button>
        </div>
        <div class="gate-help" title="Two frozen trained policies, same regime, same architecture except the second has an extra visited-cells bitmap input. Matched-pair Python A/B at 20×20 / 2800 episodes: the memory variant wins on 3/3 seeds with a mean +19.4% over the baseline.">
          Both models run as pure inference. The memory variant adds the agent's occupancy bitmap as a third input tensor; same regime, same LoopGate.
        </div>
      </div>

      <div class="panel-section">
        <h3>Q-values <span class="section-sub">(8 actions)</span></h3>
        <div id="qbars" class="qbars"></div>
        <div class="qbar-legend">
          ${DIRECTIONS_LABELS.map((d, i) => `<span class="qlabel">${i}=${d}</span>`).join('')}
        </div>
      </div>

      <div class="panel-section">
        <h3>Run</h3>
        <div class="button-row">
          <button id="btn-start" class="action-btn action-primary">Start</button>
          <button id="btn-stop" class="action-btn" disabled>Stop</button>
          <button id="btn-reset" class="action-btn">Reset</button>
        </div>
        <label class="slider-label">Speed: <span id="speed-val">15</span> Hz
          <input id="slider-speed" type="range" min="1" max="60" value="15" />
        </label>
      </div>

      <div class="panel-section">
        <h3>Compare trained vs random</h3>
        <button id="btn-compare" class="action-btn">Run 30 episodes (current layout)</button>
        <div class="ratio-display">
          trained vs random: <span id="ratio-value">—</span>
        </div>
      </div>

      <div class="panel-section">
        <h3>Loop gate</h3>
        <label class="toggle-label">
          <input id="toggle-gate" type="checkbox" checked />
          Enable diagnose-then-mask gate
        </label>
        <label class="toggle-label">
          <input id="toggle-taboo" type="checkbox" />
          Basin-cell taboo (variance reducer)
        </label>
        <table class="stats-table">
          <tr><td>Status</td><td><span id="gate-status">idle</span></td></tr>
          <tr><td>Fires (ep)</td><td><span id="gate-fires">0</span></td></tr>
          <tr><td>Masked</td><td><span id="gate-masked">—</span></td></tr>
          <tr><td>Taboo blocks</td><td><span id="gate-taboo">—</span></td></tr>
          <tr><td>speed / gold_W</td><td><span id="gate-speed">—</span></td></tr>
        </table>
        <div class="gate-help" title="When the agent stalls in a small basin (low speed, no gold), the gate masks the looping directions and picks the least-recently-visited cell until it escapes. The optional taboo mask additionally bans the 5×5 neighbourhood of escaped basins for ~14 ticks, which trades peak performance for predictability.">
          Oscillation gate + optional taboo (5×5 decay). On adversarial layouts the taboo lifts the trained-vs-random ratio off the floor at the cost of capping the peak on cooperative layouts.
        </div>
      </div>

      <div class="panel-section">
        <h3>View</h3>
        <label class="toggle-label"><input id="toggle-belief" type="checkbox" checked /> Belief heat-map</label>
        <label class="toggle-label"><input id="toggle-sensor" type="checkbox" checked /> Sensor footprint</label>
        <label class="toggle-label"><input id="toggle-reveal" type="checkbox" /> Reveal all (spoiler)</label>
      </div>

      <div class="panel-section">
        <h3>Stats</h3>
        <table class="stats-table">
          <tr><td>Collected</td><td><span id="stat-collected">0.00</span></td></tr>
          <tr><td>Value left</td><td><span id="stat-value-left">0.00</span></td></tr>
          <tr><td>Steps</td><td><span id="stat-steps">0</span> / ${MAX_STEPS}</td></tr>
          <tr><td>Episodes</td><td><span id="stat-episodes">0</span></td></tr>
          <tr><td>Total collected</td><td><span id="stat-total">0.00</span></td></tr>
          <tr><td>‖h‖</td><td><span id="stat-hnorm">0.00</span></td></tr>
        </table>
      </div>

      <div class="panel-section">
        <h3>Paint</h3>
        <div class="brush-row">
          <button data-brush="gold" class="brush-btn brush-gold active">Gold</button>
          <button data-brush="stone" class="brush-btn brush-stone">Stone</button>
          <button data-brush="free" class="brush-btn brush-free">Erase</button>
        </div>
        <div class="button-row">
          <button id="btn-generate" class="action-btn">Generate cluster layout</button>
          <button id="btn-clear" class="action-btn">Clear</button>
        </div>
      </div>
    `;
  }

  private wire(): void {
    // Model selector. Two buttons; clicking one swaps the active model and
    // flips the .active class. The actual swap happens in main.ts via the
    // onSelectModel callback (which stops any running episode and resets
    // the recurrence on the new model).
    this.modelBaseBtn = this.el.querySelector<HTMLButtonElement>('#model-base')!;
    this.modelMemBtn = this.el.querySelector<HTMLButtonElement>('#model-mem')!;
    this.modelBaseBtn.addEventListener('click', () => {
      this.cb.onSelectModel?.('base');
    });
    this.modelMemBtn.addEventListener('click', () => {
      this.cb.onSelectModel?.('mem');
    });

    // Brush buttons.
    let currentBrush: Brush = 'gold';
    const brushBtns = this.el.querySelectorAll<HTMLButtonElement>('.brush-btn');
    brushBtns.forEach((b) => {
      b.addEventListener('click', () => {
        brushBtns.forEach((x) => x.classList.remove('active'));
        b.classList.add('active');
        currentBrush = b.dataset.brush as Brush;
        this.cb.onBrush(currentBrush);
      });
    });
    this.el.querySelector<HTMLButtonElement>('#btn-generate')!.addEventListener('click', () => this.cb.onGenerate());
    this.el.querySelector<HTMLButtonElement>('#btn-clear')!.addEventListener('click', () => this.cb.onClear());

    // Lifecycle.
    this.startBtn = this.el.querySelector<HTMLButtonElement>('#btn-start')!;
    this.stopBtn = this.el.querySelector<HTMLButtonElement>('#btn-stop')!;
    this.resetBtn = this.el.querySelector<HTMLButtonElement>('#btn-reset')!;
    this.compareBtn = this.el.querySelector<HTMLButtonElement>('#btn-compare')!;
    this.startBtn.addEventListener('click', () => this.cb.onStart());
    this.stopBtn.addEventListener('click', () => this.cb.onStop());
    this.resetBtn.addEventListener('click', () => this.cb.onReset());
    this.compareBtn.addEventListener('click', () => { void this.cb.onCompare(); });

    // Speed slider.
    const speedSlider = this.el.querySelector<HTMLInputElement>('#slider-speed')!;
    const speedVal = this.el.querySelector<HTMLElement>('#speed-val')!;
    speedSlider.addEventListener('input', () => {
      const hz = parseInt(speedSlider.value, 10);
      speedVal.textContent = String(hz);
      this.cb.onSpeed(hz);
    });

    // Toggles.
    this.el.querySelector<HTMLInputElement>('#toggle-belief')!.addEventListener('change', (e) => {
      this.cb.onShowBelief((e.target as HTMLInputElement).checked);
    });
    this.el.querySelector<HTMLInputElement>('#toggle-sensor')!.addEventListener('change', (e) => {
      this.cb.onShowSensor((e.target as HTMLInputElement).checked);
    });
    this.el.querySelector<HTMLInputElement>('#toggle-reveal')!.addEventListener('change', (e) => {
      this.cb.onReveal((e.target as HTMLInputElement).checked);
    });

    // Gate toggle.
    this.gateToggleEl = this.el.querySelector<HTMLInputElement>('#toggle-gate')!;
    this.gateToggleEl.addEventListener('change', (e) => {
      const enabled = (e.target as HTMLInputElement).checked;
      this.cb.onToggleGate?.(enabled);
    });

    // Taboo toggle.
    this.gateTabooToggleEl = this.el.querySelector<HTMLInputElement>('#toggle-taboo')!;
    this.gateTabooToggleEl.addEventListener('change', (e) => {
      const enabled = (e.target as HTMLInputElement).checked;
      this.cb.onToggleTaboo?.(enabled);
    });

    // Cache stat elements.
    this.collectedEl = this.el.querySelector<HTMLElement>('#stat-collected')!;
    this.valueLeftEl = this.el.querySelector<HTMLElement>('#stat-value-left')!;
    this.stepsEl = this.el.querySelector<HTMLElement>('#stat-steps')!;
    this.episodesEl = this.el.querySelector<HTMLElement>('#stat-episodes')!;
    this.totalCollectedEl = this.el.querySelector<HTMLElement>('#stat-total')!;
    this.hNormEl = this.el.querySelector<HTMLElement>('#stat-hnorm')!;
    this.qBarsEl = this.el.querySelector<HTMLElement>('#qbars')!;
    this.ratioEl = this.el.querySelector<HTMLElement>('#ratio-value')!;
    this.gateStatusEl = this.el.querySelector<HTMLElement>('#gate-status')!;
    this.gateFiresEl = this.el.querySelector<HTMLElement>('#gate-fires')!;
    this.gateMaskedEl = this.el.querySelector<HTMLElement>('#gate-masked')!;
    this.gateSpeedEl = this.el.querySelector<HTMLElement>('#gate-speed')!;
    this.gateTabooEl = this.el.querySelector<HTMLElement>('#gate-taboo')!;

    // Build the 8 Q-bars once.
    for (let a = 0; a < N_ACTIONS; a++) {
      const bar = document.createElement('div');
      bar.className = 'qbar';
      const fill = document.createElement('div');
      fill.className = 'qbar-fill';
      bar.appendChild(fill);
      const label = document.createElement('span');
      label.className = 'qbar-num';
      label.textContent = '—';
      bar.appendChild(label);
      this.qBarsEl.appendChild(bar);
    }
  }

  /** Update stats and Q-bars from a tick's data. Called from the rAF loop. */
  update(u: PanelUpdate): void {
    this.collectedEl.textContent = u.collected.toFixed(2);
    this.valueLeftEl.textContent = u.valueLeft.toFixed(2);
    this.stepsEl.textContent = String(u.steps);
    this.episodesEl.textContent = String(u.episodes);
    this.totalCollectedEl.textContent = u.totalCollected.toFixed(2);
    this.hNormEl.textContent = u.hNorm.toFixed(2);

    // Q-bars: normalise so the tallest fills the bar height.
    let maxAbs = 1e-6;
    for (let a = 0; a < N_ACTIONS; a++) maxAbs = Math.max(maxAbs, Math.abs(u.qValues[a]));
    const bars = this.qBarsEl.children;
    for (let a = 0; a < N_ACTIONS; a++) {
      const bar = bars[a] as HTMLElement;
      const fill = bar.firstChild as HTMLElement;
      const num = bar.lastChild as HTMLElement;
      const v = u.qValues[a];
      const frac = v / maxAbs;          // in [-1, 1]
      const heightPct = Math.abs(frac) * 100;
      fill.style.height = `${heightPct}%`;
      fill.classList.toggle('neg', frac < 0);
      // Dim the fill if the gate is masking this action this tick.
      const masked = u.gate?.masked.has(a) ?? false;
      fill.classList.toggle('masked', masked);
      num.textContent = v.toFixed(2);
    }

    // Lifecycle button state.
    this.startBtn.disabled = u.running;
    this.stopBtn.disabled = !u.running;
    this.compareBtn.disabled = u.comparing || u.running;
    this.compareBtn.textContent = u.comparing ? 'Running…' : 'Run 30 episodes (current layout)';

    // Model selector: highlight the active variant.
    this.modelBaseBtn.classList.toggle('active', u.activeModel === 'base');
    this.modelMemBtn.classList.toggle('active', u.activeModel === 'mem');

    if (u.lastRatio !== null) {
      this.ratioEl.textContent = `${u.lastRatio.toFixed(2)}×`;
    }

    // Gate HUD.
    this.gateToggleEl.checked = u.gateEnabled;
    this.gateTabooToggleEl.checked = u.tabooEnabled;
    // The taboo toggle is only meaningful when the gate itself is on.
    this.gateTabooToggleEl.disabled = !u.gateEnabled;
    if (u.gateEnabled && u.gate) {
      this.gateStatusEl.textContent = u.gate.active ? 'ESCAPING' : 'idle';
      this.gateStatusEl.classList.toggle('gate-active', u.gate.active);
      this.gateFiresEl.textContent = String(u.gate.fireCount);
      const maskedArr = Array.from(u.gate.masked).sort((a, b) => a - b);
      this.gateMaskedEl.textContent = maskedArr.length > 0
        ? maskedArr.map((a) => DIRECTIONS_LABELS[a]).join(' ')
        : '—';
      this.gateSpeedEl.textContent = `${u.gate.speed.toFixed(2)} / ${u.gate.goldWindow.toFixed(2)}`;
      this.gateTabooEl.textContent = u.tabooEnabled
        ? String(u.gate.tabooBlocks)
        : 'off';
    } else {
      this.gateStatusEl.textContent = 'off';
      this.gateStatusEl.classList.remove('gate-active');
      this.gateFiresEl.textContent = '—';
      this.gateMaskedEl.textContent = '—';
      this.gateSpeedEl.textContent = '—';
      this.gateTabooEl.textContent = '—';
    }
  }
}
